/**
 * The reports.
 *
 * Every figure here is read from the deal as it was signed. The tax columns
 * are generated by the database and the county rate is snapshotted at
 * signing, so a report run today for a month two years ago shows what was
 * actually charged then, even if the county has changed its surtax since.
 * Nothing in this file recalculates tax, on purpose.
 */

import { supabase } from "./supabase";

export interface MonthKey { year: number; month: number }

export const monthLabel = (m: MonthKey) =>
  new Date(m.year, m.month - 1, 1).toLocaleDateString("en-US",
    { month: "short", year: "numeric" });

export const monthShort = (m: MonthKey) =>
  new Date(m.year, m.month - 1, 1).toLocaleDateString("en-US", { month: "short" });

function bounds({ year, month }: MonthKey) {
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  const to = `${next.year}-${String(next.month).padStart(2, "0")}-01`;
  return { from, to };
}

/** The last n months, most recent first. */
export function recentMonths(n: number, from = new Date()): MonthKey[] {
  const out: MonthKey[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() - i, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* DR-15                                                               */
/* ------------------------------------------------------------------ */

export interface Dr15Line {
  dealNo: string;
  date: string;
  buyer: string;
  vehicle: string;
  county: string;
  grossCents: number;      // what the sale was written at
  taxableCents: number;    // after any trade allowance
  stateTaxCents: number;
  surtaxCents: number;
}

export interface Dr15 {
  month: MonthKey;
  lines: Dr15Line[];
  grossCents: number;
  exemptCents: number;     // gross less taxable: trade allowances
  taxableCents: number;
  stateTaxCents: number;
  surtaxCents: number;
  taxDueCents: number;
  byCounty: { county: string; taxableCents: number; surtaxCents: number }[];
}

/**
 * The figures behind a monthly DR-15.
 *
 * Counted by agreement date, which is the date on the contract. A deal is
 * included once it is sold; quotes, deposits and voided deals are not sales
 * and carry no tax.
 */
export async function dr15(month: MonthKey): Promise<Dr15> {
  const { from, to } = bounds(month);
  const { data, error } = await supabase
    .from("deals")
    .select(`deal_no,agreement_date,status,county_name,
             cash_price_cents,taxable_amount_cents,state_tax_cents,surtax_cents,
             trade_allowance_cents,
             buyer:customers!deals_buyer_id_fkey(first_name,last_name),
             vehicle:vehicles!deals_vehicle_id_fkey(year,make,model)`)
    .eq("status", "sold")
    .gte("agreement_date", from)
    .lt("agreement_date", to)
    .order("agreement_date");

  if (error) throw new Error(`Could not read the deals: ${error.message}`);

  const lines: Dr15Line[] = (data ?? []).map((d) => {
    const b = d.buyer as unknown as { first_name: string; last_name: string } | null;
    const v = d.vehicle as unknown as { year: number; make: string; model: string } | null;
    return {
      dealNo: d.deal_no as string,
      date: d.agreement_date as string,
      buyer: b ? `${b.first_name} ${b.last_name}` : "—",
      vehicle: v ? `${v.year} ${v.make} ${v.model}` : "—",
      county: (d.county_name as string) ?? "—",
      grossCents: (d.cash_price_cents as number) ?? 0,
      taxableCents: (d.taxable_amount_cents as number) ?? 0,
      stateTaxCents: (d.state_tax_cents as number) ?? 0,
      surtaxCents: (d.surtax_cents as number) ?? 0,
    };
  });

  const sum = (f: (l: Dr15Line) => number) => lines.reduce((n, l) => n + f(l), 0);
  const grossCents = sum((l) => l.grossCents);
  const taxableCents = sum((l) => l.taxableCents);
  const stateTaxCents = sum((l) => l.stateTaxCents);
  const surtaxCents = sum((l) => l.surtaxCents);

  const counties = new Map<string, { taxableCents: number; surtaxCents: number }>();
  for (const l of lines) {
    const c = counties.get(l.county) ?? { taxableCents: 0, surtaxCents: 0 };
    c.taxableCents += l.taxableCents;
    c.surtaxCents += l.surtaxCents;
    counties.set(l.county, c);
  }

  return {
    month, lines, grossCents, taxableCents, stateTaxCents, surtaxCents,
    // What was not taxed. With trade-ins this is the allowance; without them
    // it is zero, and a non-zero figure here is worth understanding.
    exemptCents: grossCents - taxableCents,
    taxDueCents: stateTaxCents + surtaxCents,
    byCounty: [...counties.entries()]
      .map(([county, v]) => ({ county, ...v }))
      .sort((a, b) => b.taxableCents - a.taxableCents),
  };
}

/* ------------------------------------------------------------------ */
/* Gross per deal                                                      */
/* ------------------------------------------------------------------ */

export interface GrossLine {
  dealNo: string;
  date: string;
  vehicle: string;
  stockNo: string;
  soldCents: number;
  costCents: number;
  grossCents: number;
  daysHeld: number | null;
}

/**
 * What each car actually made.
 *
 * Cost is the vehicle's total cost, which since patch 12 includes every
 * expense line — transport, parts, paint. Before that it was the purchase
 * price alone and this number would have flattered every deal.
 */
export async function grossPerDeal(month: MonthKey): Promise<GrossLine[]> {
  const { from, to } = bounds(month);
  const { data, error } = await supabase
    .from("deals")
    .select(`deal_no,agreement_date,cash_price_cents,
             vehicle:vehicles!deals_vehicle_id_fkey(
               stock_no,year,make,model,total_cost_cents,purchase_date)`)
    .eq("status", "sold")
    .gte("agreement_date", from)
    .lt("agreement_date", to)
    .order("agreement_date");

  if (error) throw new Error(`Could not read the deals: ${error.message}`);

  return (data ?? []).map((d) => {
    const v = d.vehicle as unknown as {
      stock_no: string; year: number; make: string; model: string;
      total_cost_cents: number; purchase_date: string;
    } | null;
    const sold = (d.cash_price_cents as number) ?? 0;
    const cost = v?.total_cost_cents ?? 0;
    const bought = v?.purchase_date ? new Date(v.purchase_date) : null;
    const signed = new Date(d.agreement_date as string);
    return {
      dealNo: d.deal_no as string,
      date: d.agreement_date as string,
      vehicle: v ? `${v.year} ${v.make} ${v.model}` : "—",
      stockNo: v?.stock_no ?? "—",
      soldCents: sold,
      costCents: cost,
      grossCents: sold - cost,
      daysHeld: bought
        ? Math.max(0, Math.round((signed.getTime() - bought.getTime()) / 86_400_000))
        : null,
    };
  });
}

/* ------------------------------------------------------------------ */
/* The sales record book                                               */
/* ------------------------------------------------------------------ */

export interface RecordBookRow {
  stockNo: string;
  vin: string;
  vehicle: string;
  purchasedOn: string;
  purchasedFrom: string;
  soldOn: string | null;
  soldTo: string | null;
  dealNo: string | null;
  titleStatus: string;
}

/**
 * Every vehicle bought and sold, which Florida requires a dealer to keep.
 *
 * Not filtered to a month: the book is the whole record. Sold and unsold are
 * both in it — a car bought and still on the lot is part of the record too.
 */
export async function recordBook(): Promise<RecordBookRow[]> {
  const { data, error } = await supabase
    .from("vehicles")
    .select(`stock_no,vin,year,make,model,purchase_date,purchased_from,title_status,
             deals:deals!deals_vehicle_id_fkey(
               deal_no,agreement_date,status,
               buyer:customers!deals_buyer_id_fkey(first_name,last_name))`)
    .order("purchase_date", { ascending: false });

  if (error) throw new Error(`Could not read the vehicles: ${error.message}`);

  return (data ?? []).map((v) => {
    const deals = (v.deals ?? []) as unknown as {
      deal_no: string; agreement_date: string; status: string;
      buyer: { first_name: string; last_name: string } | null;
    }[];
    const sale = deals.find((d) => d.status === "sold");
    return {
      stockNo: v.stock_no as string,
      vin: v.vin as string,
      vehicle: `${v.year} ${v.make} ${v.model}`,
      purchasedOn: v.purchase_date as string,
      purchasedFrom: (v.purchased_from as string) ?? "—",
      soldOn: sale?.agreement_date ?? null,
      soldTo: sale?.buyer ? `${sale.buyer.first_name} ${sale.buyer.last_name}` : null,
      dealNo: sale?.deal_no ?? null,
      titleStatus: v.title_status as string,
    };
  });
}

/* ------------------------------------------------------------------ */
/* Twelve months at a glance                                           */
/* ------------------------------------------------------------------ */

export interface MonthTotals {
  month: MonthKey;
  units: number;
  grossSalesCents: number;
  /** Everything billed: price, fees and tax — what the customer actually
      paid, rather than what the car was written at. */
  invoicedCents: number;
  profitCents: number;
  taxDueCents: number;
}

export async function trailingMonths(count = 12): Promise<MonthTotals[]> {
  const months = recentMonths(count).reverse();
  const first = bounds(months[0]!).from;

  const { data, error } = await supabase
    .from("deals")
    .select(`agreement_date,cash_price_cents,state_tax_cents,surtax_cents,
             total_amount_due_cents,
             vehicle:vehicles!deals_vehicle_id_fkey(total_cost_cents)`)
    .eq("status", "sold")
    .gte("agreement_date", first);

  if (error) throw new Error(`Could not read the deals: ${error.message}`);

  const empty = () => ({ units: 0, grossSalesCents: 0, invoicedCents: 0,
                         profitCents: 0, taxDueCents: 0 });
  const buckets = new Map(months.map((m) => [`${m.year}-${m.month}`, empty()]));

  for (const d of data ?? []) {
    const dt = new Date(d.agreement_date as string);
    const key = `${dt.getFullYear()}-${dt.getMonth() + 1}`;
    const b = buckets.get(key);
    if (!b) continue;
    const v = d.vehicle as unknown as { total_cost_cents: number } | null;
    const price = (d.cash_price_cents as number) ?? 0;
    b.units += 1;
    b.grossSalesCents += price;
    b.invoicedCents += (d.total_amount_due_cents as number) ?? 0;
    b.profitCents += price - (v?.total_cost_cents ?? 0);
    b.taxDueCents += ((d.state_tax_cents as number) ?? 0)
                   + ((d.surtax_cents as number) ?? 0);
  }

  return months.map((m) => ({ month: m, ...buckets.get(`${m.year}-${m.month}`)! }));
}

/* ------------------------------------------------------------------ */
/* Taking it away                                                      */
/* ------------------------------------------------------------------ */

/** Quote a CSV field: anything with a comma, quote or newline needs wrapping,
    and a quote inside is doubled. */
function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(headers: string[], rows: unknown[][]): string {
  return [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
}

export function downloadCsv(filename: string, csv: string) {
  // The BOM makes Excel open UTF-8 correctly instead of mangling accents.
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
